import React from 'react';
import { Heart, MessageCircle, Share2, MoreHorizontal } from 'lucide-react';

interface PinterestPreviewProps {
  content: string | any;
  brandName?: string;
  brandLogo?: string;
  mediaUrl?: string;
  mediaUrls?: string[];
  hideEngagementCounts?: boolean;
  platformData?: any;
}

export const PinterestPreview: React.FC<PinterestPreviewProps> = ({
  content,
  brandName,
  brandLogo,
  mediaUrl,
  mediaUrls,
  hideEngagementCounts = false,
  platformData
}) => {
  // Use the first image if multiple are provided
  const imageUrl = mediaUrls && mediaUrls.length > 0 ? mediaUrls[0] : mediaUrl;

  // Extract Pinterest-specific data if available
  let pinterestTitle = '';
  let pinterestDescription = '';
  let pinterestHashtags: string[] = [];

  if (platformData && platformData.platforms && platformData.platforms.pinterest) {
    const pinterestSettings = platformData.platforms.pinterest.settings;
    if (pinterestSettings) {
      pinterestTitle = pinterestSettings.title || '';
      pinterestDescription = pinterestSettings.description || '';
    }
    if (platformData.hashtags) {
      pinterestHashtags = platformData.hashtags;
    }
  }

  // Extract title and description from content (fallback)
  const contentString = typeof content === 'string' ? content : JSON.stringify(content);
  const lines = contentString.split('\n').filter(line => line.trim());

  // Use Pinterest-specific title if available, otherwise fallback to content
  const title = pinterestTitle || lines[0] || 'Pin Title';
  const description = pinterestDescription || lines.slice(1).join('\n') || 'Pin description';
  
  // Keep full title and description - CSS will handle overflow with "See more"

  return (
    <div className="max-w-sm mx-auto bg-white rounded-lg overflow-hidden shadow-lg">
      {/* Main Image - Goes to the top */}
      <div className="relative">
        {imageUrl ? (
          <img 
            src={imageUrl} 
            alt="Pin content"
            className="w-full aspect-[4/5] object-cover"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.style.display = 'none';
              target.nextElementSibling?.classList.remove('hidden');
            }}
          />
        ) : null}
        
        {/* Fallback placeholder */}
        <div className={`w-full aspect-[4/5] bg-gradient-to-br from-red-400 to-pink-500 flex items-center justify-center ${imageUrl ? 'hidden' : ''}`}>
          <div className="text-white text-center">
            <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center mb-2">
              <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
              </svg>
            </div>
            <p className="text-sm">Pin Image</p>
          </div>
        </div>
      </div>

      {/* Interaction Bar */}
      <div className="px-4 py-3 bg-white">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-4">
            <button className="flex items-center gap-1 text-black hover:text-red-500 transition-colors">
              <Heart className="w-5 h-5" />
              {!hideEngagementCounts && <span className="text-sm text-black">0</span>}
            </button>
            <button className="flex items-center gap-1 text-black hover:text-blue-500 transition-colors">
              <MessageCircle className="w-5 h-5" />
              {!hideEngagementCounts && <span className="text-sm text-black">0</span>}
            </button>
            <button className="flex items-center gap-1 text-black hover:text-green-500 transition-colors">
              <Share2 className="w-5 h-5" />
            </button>
            <button className="flex items-center gap-1 text-black hover:text-gray-800 transition-colors">
              <MoreHorizontal className="w-5 h-5" />
            </button>
          </div>
          <button className="bg-red-600 text-white px-4 py-1 rounded-full text-sm font-medium hover:bg-red-700 transition-colors">
            Save
          </button>
        </div>

        {/* Source Info */}
        <div className="mb-2">
          <div className="flex items-center gap-2 mb-1">
            {brandLogo ? (
              <img src={brandLogo} alt={brandName || 'Brand'} className="w-6 h-6 rounded-full" />
            ) : (
              <div className="w-6 h-6 bg-gray-300 rounded-full flex items-center justify-center">
                <span className="text-xs text-black">B</span>
              </div>
            )}
            <span className="text-sm font-medium text-black">{brandName || 'Brand Name'}</span>
          </div>
          <p className="text-sm font-bold text-black mb-1 truncate">{title}</p>
          <div className="relative mb-2">
            <p className="text-sm text-black pr-16 overflow-hidden whitespace-nowrap relative"
               style={{
                 maskImage: 'linear-gradient(to right, black 70%, transparent 100%)',
                 WebkitMaskImage: 'linear-gradient(to right, black 70%, transparent 100%)'
               }}>
              {description}
            </p>
            <span className="absolute right-0 top-1/2 -translate-y-1/2 text-sm text-black bg-white pl-2">
              see more
            </span>
          </div>
          <div className="flex items-center gap-2 text-sm text-black">
            <span>Show translation</span>
          </div>
          {pinterestHashtags && pinterestHashtags.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1">
              {pinterestHashtags.slice(0, 3).map((hashtag: string, index: number) => (
                <span key={index} className="text-xs text-blue-600 font-medium">
                  #{hashtag}
                </span>
              ))}
              {pinterestHashtags.length > 3 && (
                <span className="text-xs text-gray-500">
                  +{pinterestHashtags.length - 3} more
                </span>
              )}
            </div>
          )}
        </div>

        {/* Visit Site Button */}
        <button className="w-full bg-gray-200 text-gray-800 py-2 rounded-lg text-sm font-medium hover:bg-gray-300 transition-colors flex items-center justify-center gap-2">
          Visit site
        </button>
      </div>
    </div>
  );
};
